import axios from "./_axios";
import { API } from "./apiEndPoints";
import { attachmentCardFromRef } from "@components/AskAQuestionPrompt/resolveAttachmentPreview";

const toIdString = (value) => {
  if (value == null || value === "") {
    return null;
  }
  return String(value);
};

export const getAskAiAccountId = () => {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") {
      return null;
    }
    return toIdString(JSON.parse(raw));
  } catch {
    try {
      const raw = localStorage.getItem("currentAccountID");
      return raw ? toIdString(raw) : null;
    } catch {
      return null;
    }
  }
};

export const getAskAiAccessId = () => {
  try {
    const raw = localStorage.getItem("accessPermission");
    if (!raw) {
      return null;
    }
    const id = JSON.parse(raw)?.id;
    return id == null || id === "" ? null : Number(id);
  } catch {
    return null;
  }
};

const conversationsBasePath = (accountId) =>
  `${API.ACCOUNT_LIST_V2}${accountId}/ask-ai-conversations/`;

/**
 * Filename characters the IQ ``/ask`` endpoint accepts inside an S3 key. IQ
 * refuses any other character ("Refusing to fetch an attachment key"), and
 * Django builds the stored key from the uploaded file name — so a name such as
 * ``image (20).png`` yields a key IQ rejects. We keep the same alphabet here.
 */
const UNSAFE_ATTACHMENT_NAME_CHARS = /[^A-Za-z0-9._-]+/g;

/**
 * Sanitise an upload file name so the S3 key Django derives from it stays
 * within the alphabet IQ accepts. The extension is preserved; runs of
 * disallowed characters collapse to a single ``-`` and leading/trailing
 * separators are trimmed.
 */
export const sanitizeAttachmentFileName = (name) => {
  const raw = String(name ?? "").trim();
  if (!raw) {
    return "attachment";
  }
  const dot = raw.lastIndexOf(".");
  const hasExtension = dot > 0 && dot < raw.length - 1;
  const stem = hasExtension ? raw.slice(0, dot) : raw;
  const extension = hasExtension ? raw.slice(dot + 1) : "";
  const cleanStem = stem.replace(UNSAFE_ATTACHMENT_NAME_CHARS, "-").replace(/^-+|-+$/g, "");
  const cleanExtension = extension.replace(UNSAFE_ATTACHMENT_NAME_CHARS, "");
  const safeStem = cleanStem || "attachment";
  return cleanExtension ? `${safeStem}.${cleanExtension}` : safeStem;
};

/**
 * Return the file to upload, renamed when its name holds characters IQ would
 * reject. The bytes, MIME type and last-modified time are preserved; an
 * already-safe name keeps the original ``File`` instance.
 */
const safeAskAiAttachmentFile = (file) => {
  if (!file) {
    return file;
  }
  const safeName = sanitizeAttachmentFileName(file.name);
  if (safeName === file.name) {
    return file;
  }
  try {
    return new File([file], safeName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
};

/**
 * Maps Django attachment upload / history ref shape to AskAQuestionAttachmentCard props.
 */
export const mapAskAiAttachmentForCard = (attachment) => attachmentCardFromRef(attachment);

/**
 * Derive the storage key from a preview URL when the upload response does not
 * expose ``s3_key`` (the current Django read serializer omits it). The signed
 * CloudFront/S3 URL carries the object key as its path, e.g.
 * ``…/ask-ai/accounts/4/accesses/4/attachments/<id>/image%20(14).png``.
 */
const s3KeyFromAttachmentUrl = (url) => {
  if (typeof url !== "string" || !url) {
    return null;
  }
  try {
    const { pathname } = new URL(url, "https://ask-ai.invalid");
    const key = decodeURIComponent(pathname).replace(/^\/+/, "");
    return key.startsWith("ask-ai/") ? key : null;
  } catch {
    return null;
  }
};

/**
 * Maps an upload response to the reference the IQ microservice ``/ask``
 * endpoint expects for multimodal input (IQ Chat Rule 12). Only the storage
 * reference travels — never the file bytes. Prefers an explicit ``s3_key`` and
 * falls back to deriving it from the preview ``url``; returns ``null`` only
 * when neither yields a key.
 *
 * IQ accepts either ``mime_type`` or ``content_type``; we normalise to
 * ``mime_type`` and tolerate the Django read field ``content_type_mime``.
 */
export const mapAskAiAttachmentForIQ = (attachment) => {
  if (!attachment || typeof attachment !== "object") {
    return null;
  }
  const s3Key = attachment.s3_key || attachment.s3Key || s3KeyFromAttachmentUrl(attachment.url);
  if (!s3Key) {
    return null;
  }
  const mimeType =
    attachment.mime_type || attachment.content_type || attachment.content_type_mime || "";
  const ref = {
    s3_key: s3Key,
    file_name: attachment.file_name || attachment.name || "attachment",
  };
  if (mimeType) {
    ref.mime_type = mimeType;
  }
  if (attachment.bucket) {
    ref.bucket = attachment.bucket;
  }
  return ref;
};

export const describeAskAiConversationFailure = (error) => {
  const status = error?.response?.status ?? error?.status;
  const data = error?.response?.data ?? error?.data;
  const detail =
    typeof data === "string"
      ? data
      : typeof data?.detail === "string"
      ? data.detail
      : Array.isArray(data?.detail)
      ? data.detail.join(" ")
      : "";

  if (detail.includes("Ask AI conversations are not available")) {
    return "Ask AI conversations are not available for this account. If this keeps happening, confirm with your administrator that the feature is enabled on the server.";
  }
  if (status === 403) {
    return "You do not have permission to send Ask AI messages for this account.";
  }
  if (status === 404) {
    return "That Ask AI conversation could not be found. Start a new chat and try again.";
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  return "I apologize, but I encountered an error while processing your request. Please try again.";
};

export const createAskAiConversation = async ({ accessId, title, signal } = {}) => {
  const accountId = getAskAiAccountId();
  const resolvedAccessId = accessId ?? getAskAiAccessId();
  if (!accountId || resolvedAccessId == null) {
    throw new Error("Account and access are required to start an Ask AI conversation.");
  }

  const payload = { access_id: resolvedAccessId };
  if (title != null && String(title).trim()) {
    payload.title = String(title).trim();
  }

  const result = await axios()({
    method: "post",
    url: conversationsBasePath(accountId),
    data: payload,
    signal,
  });
  return result.data;
};

export const uploadAskAiAttachment = async ({ file, accessId, signal } = {}) => {
  const accountId = getAskAiAccountId();
  const resolvedAccessId = accessId ?? getAskAiAccessId();
  if (!accountId || resolvedAccessId == null) {
    throw new Error("Account and access are required to upload an attachment.");
  }
  if (!file) {
    throw new Error("A file is required to upload.");
  }

  const formData = new FormData();
  formData.append("access_id", String(resolvedAccessId));
  formData.append("file", safeAskAiAttachmentFile(file));

  const result = await axios()({
    method: "post",
    url: `${conversationsBasePath(accountId)}attachments/`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
    signal,
  });
  return result.data;
};
